import React, { useMemo } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

const pointsByRange = { "1D": 36, "1W": 42, "1M": 56 };

const seededNoise = (seed, index) => {
  const value = Math.sin(seed * 13.73 + index * 41.17) * 43758.5453;
  return value - Math.floor(value);
};

const buildData = ({ seed = 1, range = "1D" }) => {
  const points = pointsByRange[range] || 36;
  let prev = 28 + seed * 0.3;
  const baseTime = new Date();
  baseTime.setHours(9, 15, 0, 0);

  return Array.from({ length: points }, (_, i) => {
    const noise = seededNoise(seed, i) - 0.5;
    const wave = Math.sin((i / Math.max(points - 1, 1)) * Math.PI * 3.6) * 1.2;
    const drift = 0.24 + noise * 1.35 + wave;
    const value = Math.max(8, prev + drift);
    const volume = Math.max(10, Math.round(26 + Math.abs(value - prev) * 6 + seededNoise(seed + 2, i) * 8));
    prev = value;

    return {
      t: i + 1,
      timeLabel:
        range === "1D"
          ? new Date(baseTime.getTime() + i * 5 * 60 * 1000).toLocaleTimeString("en-IN", {
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            })
          : range === "1W"
            ? `D${i + 1}`
            : `P${i + 1}`,
      value: Number(value.toFixed(2)),
      volume,
    };
  });
};

const NeonTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0]?.payload;

  return (
    <div
      style={{
        minWidth: "170px",
        padding: "10px 12px",
        borderRadius: "10px",
        background: "#FFFFFF",
        border: "1px solid rgba(72, 255, 164, 0.28)",
        boxShadow: "0 10px 28px rgba(0, 0, 0, 0.35)",
        color: "#0F172A",
      }}
    >
      <div style={{ fontSize: 11, opacity: 0.85, marginBottom: 6, color: "#475569" }}>Point #{row?.t}</div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12 }}>
        <span>Trend</span>
        <span style={{ fontWeight: 700 }}>{Number(row?.value || 0).toFixed(2)}</span>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: 12, marginTop: 4 }}>
        <span>Volume</span>
        <span style={{ fontWeight: 700 }}>{Number(row?.volume || 0)}</span>
      </div>
    </div>
  );
};

const TodaysPLBreakdownChart = ({ seed = 1, range = "1D" }) => {
  const data = useMemo(() => buildData({ seed, range }), [seed, range]);

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
          <defs>
            <linearGradient id="barGlow" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#4CB36B" stopOpacity={0.95} />
              <stop offset="100%" stopColor="#157A43" stopOpacity={0.45} />
            </linearGradient>
          </defs>

          <CartesianGrid stroke="rgba(125, 211, 165, 0.12)" strokeDasharray="3 3" />
          <XAxis
            dataKey="timeLabel"
            axisLine={false}
            tickLine={false}
            tick={{ fill: "#757575", fontSize: 10 }}
            minTickGap={22}
          />
          <YAxis hide />
          <Tooltip content={<NeonTooltip />} cursor={{ stroke: "rgba(110, 255, 187, 0.35)", strokeWidth: 1 }} />

          <Bar
            dataKey="volume"
            barSize={11}
            radius={[4, 4, 0, 0]}
            fill="url(#barGlow)"
            isAnimationActive
            animationDuration={950}
            animationEasing="ease-out"
          />

          <Line
            type="monotone"
            dataKey="value"
            stroke="#59F3A1"
            strokeWidth={2.6}
            dot={{ r: 2.2, fill: "#8CFFD1", stroke: "#2ACE7D", strokeWidth: 1 }}
            activeDot={{ r: 4, fill: "#A8FFE0", stroke: "#2ACE7D", strokeWidth: 1.4 }}
            isAnimationActive
            animationDuration={1100}
            animationEasing="ease-in-out"
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TodaysPLBreakdownChart;
